import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { CommandPalette, useKeybindings } from 'agentable-canvas/general';
import { listSites, type SiteListItem } from '../../lib/sites-api';
import { useStudioCommands } from '../../hooks/useStudioCommands';

/**
 * App-level command palette (Cmd/Ctrl+K) — orchestration-style modal with
 * categorized studio commands. Available on home and canvas surfaces.
 */
export function StudioCommandPalette(): ReactElement {
  const [open, setOpen] = useState(false);
  const [sites, setSites] = useState<SiteListItem[]>([]);

  const setPaletteOpen = useCallback((next: boolean) => setOpen(next), []);
  const { commands, bindings } = useStudioCommands({
    setPaletteOpen,
    sites,
  });

  useKeybindings(bindings, { disabled: open });

  // Capture-phase guard: Ctrl/Cmd+K must open the palette, never legacy
  // "new chat" handlers that may still exist in older overlay code paths.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (open) return;
      if (event.key.toLowerCase() !== 'k') return;
      if (!event.ctrlKey && !event.metaKey) return;
      if (event.altKey || event.shiftKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setPaletteOpen(true);
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [open, setPaletteOpen]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listSites()
      .then((loaded) => {
        if (!cancelled) setSites(loaded);
      })
      .catch(() => {
        if (!cancelled) setSites([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <div data-testid="studio-command-palette">
      <CommandPalette
        open={open}
        onClose={() => setOpen(false)}
        commands={commands}
        placeholder="Type a command…"
      />
    </div>
  );
}
